/** Text edits behind the article editor's buttons and keys, as pure functions. */
const INLINE = {
    bold: { before: '**', after: '**', placeholder: 'жирный текст' },
    italic: { before: '_', after: '_', placeholder: 'курсив' },
    code: { before: '`', after: '`', placeholder: 'код' },
    link: { before: '[', after: '](https://)', placeholder: 'текст ссылки' },
};
function editInline(value, start, end, action) {
    const { before, after, placeholder } = INLINE[action];
    const content = value.slice(start, end) || placeholder;
    const contentStart = start + before.length;
    const contentEnd = contentStart + content.length;
    // A link around a selected text: what is left to type is its address.
    if (action === 'link' && end > start) {
        return {
            value: `${value.slice(0, start)}${before}${content}${after}${value.slice(end)}`,
            selectionStart: contentEnd + 2,
            selectionEnd: contentEnd + after.length - 1,
        };
    }
    return {
        value: `${value.slice(0, start)}${before}${content}${after}${value.slice(end)}`,
        selectionStart: contentStart,
        selectionEnd: contentEnd,
    };
}
function editBlock(value, start, end, action) {
    const lineStart = value.lastIndexOf('\n', Math.max(0, start - 1)) + 1;
    const nextLineBreak = value.indexOf('\n', end);
    const lineEnd = nextLineBreak === -1 ? value.length : nextLineBreak;
    const lines = value.slice(lineStart, lineEnd).split('\n');
    const fallback = action === 'heading' ? 'Заголовок' : action === 'heading3' ? 'Подзаголовок' : 'Пункт';
    const sourceLines = lines.length === 1 && lines[0] === '' ? [fallback] : lines;
    const prefix = (index) => {
        if (action === 'heading')
            return '## ';
        if (action === 'heading3')
            return '### ';
        if (action === 'bullet-list')
            return '- ';
        if (action === 'ordered-list')
            return `${index + 1}. `;
        return '> ';
    };
    const replacement = sourceLines.map((line, index) => `${prefix(index)}${line}`).join('\n');
    return {
        value: `${value.slice(0, lineStart)}${replacement}${value.slice(lineEnd)}`,
        selectionStart: lineStart,
        selectionEnd: lineStart + replacement.length,
    };
}
/** Fences the lines under the selection as a code block; the code stays selected. */
function fenceLines(value, start, end) {
    const lineStart = start === 0 ? 0 : value.lastIndexOf('\n', start - 1) + 1;
    const nextBreak = value.indexOf('\n', end);
    const lineEnd = nextBreak === -1 ? value.length : nextBreak;
    const code = value.slice(lineStart, lineEnd);
    return {
        value: `${value.slice(0, lineStart)}\`\`\`\n${code}\n\`\`\`${value.slice(lineEnd)}`,
        selectionStart: lineStart + 4,
        selectionEnd: lineStart + 4 + code.length,
    };
}
const TABLE_CELL = 'Столбец';
const TABLE = `| ${TABLE_CELL} | ${TABLE_CELL} |\n| --- | --- |\n| Текст | Текст |`;
/** A two-column table on its own paragraph; its first cell is selected to type over. */
function insertTable(value, start, end) {
    const at = insertBlock(value, start, end, TABLE);
    const cell = at.selectionStart - TABLE.length + 2;
    return { value: at.value, selectionStart: cell, selectionEnd: cell + TABLE_CELL.length };
}
export function applyMarkdownAction(value, start, end, action) {
    if (action === 'code-block')
        return fenceLines(value, start, end);
    if (action === 'table')
        return insertTable(value, start, end);
    return action in INLINE
        ? editInline(value, start, end, action)
        : editBlock(value, start, end, action);
}
const caretAt = (value, caret) => ({
    value,
    selectionStart: caret,
    selectionEnd: caret,
});
/** A list item or a quote line: its marker, and the marker the next line gets. */
function lineMarker(line) {
    const item = /^(\s*)([-*+]|(\d+)([.)]))(\s+)/.exec(line);
    if (item) {
        const [marker = '', indent = '', bullet = '', num, delim = '', gap = ''] = item;
        const next = num === undefined ? bullet : `${Number(num) + 1}${delim}`;
        return { marker, next: `${indent}${next}${gap}` };
    }
    const quote = /^\s*(?:>\s?)+/.exec(line);
    return quote ? { marker: quote[0], next: quote[0] } : null;
}
/** Is the line inside a fenced code block (an odd number of fences above it)? */
function inCodeBlock(value, lineStart) {
    const fences = value.slice(0, lineStart).match(/^\s*(```|~~~)/gm);
    return (fences?.length ?? 0) % 2 === 1;
}
/**
 * What Enter does at the caret, as in a text editor: a list or a quote goes on,
 * an empty item ends it, any other line starts a new paragraph (a single line
 * break would join the lines on the site). `null` — a plain line break: code
 * blocks, tables, empty lines.
 */
export function editOnEnter(value, start, end) {
    const lineStart = value.lastIndexOf('\n', start - 1) + 1;
    const nextBreak = value.indexOf('\n', start);
    const lineEnd = nextBreak === -1 ? value.length : nextBreak;
    const line = value.slice(lineStart, lineEnd);
    const before = value.slice(0, start);
    const after = value.slice(end);
    if (inCodeBlock(value, lineStart) || /^\s*(```|~~~|\|)/.test(line))
        return null;
    const marked = lineMarker(line);
    if (marked) {
        if (start - lineStart < marked.marker.length)
            return null;
        if (line.trim() === marked.marker.trim()) {
            // Nothing after the marker: the list ends, a blank line keeps the next
            // paragraph out of it.
            return caretAt(`${value.slice(0, lineStart)}\n${value.slice(lineEnd)}`, lineStart + 1);
        }
        return caretAt(`${before}\n${marked.next}${after}`, start + 1 + marked.next.length);
    }
    if (line.trim() === '')
        return null;
    return caretAt(`${before}\n\n${after}`, start + 2);
}
/** Puts a block (an image) on its own paragraph at the caret; the caret goes right after it. */
export function insertBlock(value, start, end, block) {
    const before = value.slice(0, start);
    const after = value.slice(end);
    const prefix = before === '' || before.endsWith('\n\n') ? '' : before.endsWith('\n') ? '\n' : '\n\n';
    const suffix = after === '' || after.startsWith('\n\n') ? '' : after.startsWith('\n') ? '\n' : '\n\n';
    return caretAt(`${before}${prefix}${block}${suffix}${after}`, start + prefix.length + block.length);
}
/**
 * Stands in for an image while it uploads: a same-page address, so the preview
 * can draw a loading box instead of fetching anything.
 */
export function uploadPlaceholder(id, name) {
    return `![Загрузка ${name.replace(/[[\]()\r\n]/g, '')}…](#upload-${id})`;
}
export const isUploadPlaceholderSrc = (src) => !!src?.startsWith('#upload-');
/**
 * Swaps the stand-in for the uploaded image or, when the upload failed
 * (`image` null), drops it together with the blank lines it was put between.
 */
export function settlePlaceholder(value, placeholder, image) {
    const at = value.indexOf(placeholder);
    if (at === -1)
        return value;
    const before = value.slice(0, at);
    const after = value.slice(at + placeholder.length);
    if (image !== null)
        return `${before}${image}${after}`;
    const head = before.replace(/\n+$/, '');
    const tail = after.replace(/^\n+/, '');
    return head && tail ? `${head}\n\n${tail}` : head + tail;
}
